import { useMemo, useEffect, useImperativeHandle, forwardRef } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../../messages/en-US";
import { EventFilter } from "../../../../Events/Utilities/EventsApiService";
import siteExplorerApiService from "../../../Utilities/SiteExplorerApiService";
import assetsDashboardApiService from "../../../../Dashboards/Assets/Utilities/AssetsDashboardApiService";
import { useQueries, useQuery, useQueryClient } from "react-query";
import LoadingSpinner from "../../../../Shared/Components/LoadingSpinner";
import EndpointEventsTableAssets from "../../../Components/Endpoints/EndpointEventsTableAssets";
import EndpointFlowMeterTableAssets from "../../../Components/Endpoints/EndpointFlowMeterTableAssets";
import NoDataPage from "../../NoDataPage";
import { ReactQueryKeys } from "../../../../../Constants/ClientRoutingConstants";
import EndpointStatusChart from "../EndpointStatusChart";
import { ChartToolbar } from "../../../Utilities/ChartToolbar";
import useChartStore from "../../../../../Zustand/chartStore";
import { arrayExtent } from "../../../../Shared/Utilities/ArrayUtilities";

export interface AllEndpointsActivityTabPageRef {
  refresh: () => Promise<void>;
}

interface Props {
  eventFilter: EventFilter;
  endpointsIds: string[];
  selectedAssetId: string;
  showTables: boolean;
  isFullscreen?: boolean;
  chartParentHeight?: number;
  selectedItems?: Array<{ id: string; entityType: string }>; // Optional prop for endpoint type information
  asset?: any; // Optional asset for machineryType fallback
}

interface DataSeries {
  id: string;
  text: string;
  items?: any[];
}

interface ChartData {
  dateFilter: EventFilter;
  dataChart: any[];
  visibleRange: { timestamps: number[] };
  aggregator: string;
}

interface StateChartData {
  dateFilter: EventFilter;
  alerts: any;
  statusData: any;
  timestamps: { timestamps: number[] };
}

// Custom hooks
const useDataSeries = (hasFlowRateData?: boolean) => {
  const visibleDataSeries = useChartStore(state => state.visibleDataSeries);
  const setVisibleDataSeries = useChartStore(state => state.setVisibleDataSeries);

  // For Flow Rate chart, show Volume, Volume (Temp Comp), Flow Rate, Flow Rate (Temp Comp), and Temperature options
  // For Pressure chart, show all options
  const allDataSeries: DataSeries[] = useMemo(() => 
    hasFlowRateData 
      ? [
          { id: 'volume', text: 'Volume' },
          { id: 'volumeTemperatureCompensated', text: 'Volume (Temp Comp)' },
          { id: 'flowRate', text: 'Flow Rate' },
          { id: 'flowRateTemperatureCompensated', text: 'Flow Rate (Temp Comp)' },
          { id: 'temperature', text: 'Temperature' }
        ]
      : [
          { id: 'pressureMinMax', text: 'Pressure Min/Max', items: [] },
          { id: 'pressureMean', text: 'Pressure Mean' },
          { id: 'temperature', text: 'Temperature' },
          { id: 'volume', text: 'Volume' }
        ],
    [hasFlowRateData]
  );

  const dataSeriesUpdated = (selectedItems: any[]) => {
    allDataSeries.forEach(item => {
      const isVisible = selectedItems.some(selected => selected.id === item.id);
      setVisibleDataSeries(item.id, isVisible);
    });
  };

  useEffect(() => {
    const shouldInitialize = !allDataSeries.some(item => item.id in visibleDataSeries);
    if (shouldInitialize) {
      allDataSeries.forEach(item => {
        // For Flow Rate chart, initialize with only volume visible
        // For Pressure chart, initialize with only volume visible
        setVisibleDataSeries(item.id, item.id === 'volume');
      });
    }
  }, [hasFlowRateData, allDataSeries, visibleDataSeries, setVisibleDataSeries]);

  return {
    allDataSeries,
    visibleDataSeries,
    dataSeriesUpdated,
    setVisibleDataSeries
  };
};

const useChartQueries = (props: Props, viewType: string, aggregator: string) => {
  const [assetId] = props.selectedAssetId.split('_');
  
  // Get machineryType from asset as fallback
  const assetMachineryType = props.asset?.machineryType;

  const notificationsQuery = useQuery<any>(
    [ReactQueryKeys.AllAlertsQuery, props.eventFilter],
    () => siteExplorerApiService.getEndpointsAssetNotifications(assetId, props.eventFilter, 1)
  );
  
  const statusQuery = useQuery<any>(
    ["StatusOnOffQuery", props.eventFilter],
    () => siteExplorerApiService.getAssetStatus(assetId, props.eventFilter, 0)
  );

  const endpointsQueriesTable = useQueries<any>(
    props.endpointsIds.map((endpointId) => ({
      queryKey: ['endpointTable', endpointId, props.eventFilter, aggregator],
      queryFn: () => siteExplorerApiService.getAllEndpointsEventsFlowTable(props.eventFilter, endpointId, aggregator),
      enabled: viewType === 'table'
    })) || []
  );

  const endpointsPressureQueriesTable = useQueries<any>(
    props.endpointsIds.map((endpointId) => ({
      queryKey: ['endpointPressureTable', endpointId, props.eventFilter, aggregator],
      queryFn: () => siteExplorerApiService.getEndpointPressureEventsTable(endpointId, props.eventFilter, aggregator),
      enabled: viewType === 'table'
    })) || []
  );

  // Queries for flow rate table data
  const endpointsFlowRateQueriesTable = useQueries<any>(
    props.endpointsIds.map((endpointId) => {
      const isLocomotive = assetMachineryType === 'Locomotive';
      
      // For Locomotives, use getLocoFlowMeter
      if (isLocomotive) {
        return {
          queryKey: ['locoFlowMeterTable', endpointId, props.eventFilter.startDate, props.eventFilter.endDate],
          queryFn: async () => {
            if (!props.eventFilter.startDate || !props.eventFilter.endDate) {
              throw new Error("Start date and end date are required for Locomotive flow meter data");
            }
            const data = await assetsDashboardApiService.getLocoFlowMeter(endpointId, props.eventFilter.startDate, props.eventFilter.endDate);
            return data;
          },
          enabled: viewType === 'table' && !!props.eventFilter.startDate && !!props.eventFilter.endDate
        };
      }
      
      // For regular endpoints, use getAllEndpointsEventsFlowTable (already exists in endpointsQueriesTable)
      // Return null to skip this query since it's already handled by endpointsQueriesTable
      return {
        queryKey: ['endpointFlowRateTable', endpointId, props.eventFilter, aggregator],
        queryFn: () => siteExplorerApiService.getAllEndpointsEventsFlowTable(props.eventFilter, endpointId, aggregator),
        enabled: false // Already handled by endpointsQueriesTable
      };
    }) || []
  );

  const endpointFlowRateQueries = useQueries<any>(
    props.endpointsIds.map((endpointId) => {
      // If asset machineryType is Locomotive, ALL endpoints are Locomotives
      const isLocomotive = assetMachineryType === 'Locomotive';
      
      // For Locomotives, use getLocoFlowMeterChartData
      if (isLocomotive) {
        return {
          queryKey: ['locoFlowMeterChartData', endpointId, props.eventFilter.startDate, props.eventFilter.endDate],
          queryFn: async () => {
            if (!props.eventFilter.startDate || !props.eventFilter.endDate) {
              throw new Error("Start date and end date are required for Locomotive flow meter data");
            }
            const data = await assetsDashboardApiService.getLocoFlowMeterChartData(endpointId, props.eventFilter.startDate, props.eventFilter.endDate);
            return data;
          },
          enabled: viewType !== 'table' && !!props.eventFilter.startDate && !!props.eventFilter.endDate
        };
      }
      
      // For regular endpoints, use getAllEndpointsEventsFlowChart
      return {
        queryKey: ['endpointFlowRateChart', endpointId, props.eventFilter, aggregator],
        queryFn: () => siteExplorerApiService.getAllEndpointsEventsFlowChart(props.eventFilter, endpointId, aggregator),
        enabled: viewType !== 'table'
      };
    }) || []
  );

  const endpointsPressureQueries = useQueries<any>(
    props.endpointsIds.map((endpointId) => ({
      queryKey: ['endpointPressureChart', endpointId, props.eventFilter, aggregator],
      queryFn: () => siteExplorerApiService.getEndpointPressureEventsChart(endpointId, props.eventFilter, aggregator),
      enabled: viewType !== 'table'
    })) || []
  );

  return {
    notificationsQuery,
    statusQuery,
    endpointsQueriesTable,
    endpointsPressureQueriesTable,
    endpointsFlowRateQueriesTable,
    endpointFlowRateQueries,
    endpointsPressureQueries
  };
};

// Helper function to merge volume and pressure event data
function mergeEventData({
  volumeEvent,
  pressureEvent,
  meta
}: {
  volumeEvent?: any;
  pressureEvent?: any;
  meta?: { name?: string; consumableName?: string };
}) {
  if (volumeEvent && pressureEvent) {
    return {
      ...volumeEvent,
      pressureMax: pressureEvent.pressureMax,
      pressureMean: pressureEvent.pressureMean,
      pressureMin: pressureEvent.pressureMin,
      temperature: pressureEvent.temperature,
      endpointName: meta?.name || pressureEvent.endpointName || volumeEvent.endpointName,
      consumableName: volumeEvent.consumableName,
    };
  }
  if (volumeEvent) {
    return {
      ...volumeEvent,
      pressureMax: undefined,
      pressureMean: undefined,
      pressureMin: undefined,
      temperature: undefined,
      endpointName: meta?.name || volumeEvent.endpointName,
      consumableName: volumeEvent.consumableName,
    };
  }
  if (pressureEvent) {
    return {
      timestamp: pressureEvent.timestamp,
      serialNo: pressureEvent.serialNo,
      pressureMax: pressureEvent.pressureMax,
      pressureMean: pressureEvent.pressureMean,
      pressureMin: pressureEvent.pressureMin,
      temperature: pressureEvent.temperature,
      volumeDispensed: undefined,
      volumeDispensedRound: undefined,
      endpointName: meta?.name || pressureEvent.endpointName,
      consumableName: meta?.consumableName,
    };
  }
  return null;
}

const useChartData = (
  endpointsQueriesTable: any[],
  endpointsPressureQueriesTable: any[],
  endpointsFlowRateQueriesTable: any[],
  endpointFlowRateQueries: any[],
  endpointsPressureQueries: any[],
  eventFilter: EventFilter,
  aggregator: string,
  endpointsIds: string[],
  selectedItems?: Array<{ id: string; entityType: string }>,
  asset?: any
) => {
  // Get machineryType from asset
  const assetMachineryType = asset?.machineryType;
  
  const eventTableData = useMemo(() => {
    const endpointsArray = endpointsQueriesTable.map(endpoint => endpoint.data);
    return [].concat(...endpointsArray)?.map((event: any) => ({
      ...event,
      volumeDispensedRound: event?.volumeDispensed.toFixed(2),
    }));
  }, [endpointsQueriesTable]).filter(x => x !== undefined);

  // Build a lookup map from serialNo to name and consumableName from endpointsQueriesTable
  const serialNoToMeta = useMemo(() => {
    const map: Record<string, { name?: string; consumableName?: string }> = {};
    endpointsQueriesTable.forEach(endpoint => {
      if (endpoint.data && Array.isArray(endpoint.data)) {
        endpoint.data.forEach((event: any) => {
          if (event.serialNo) {
            map[event.serialNo] = {
              name: event.name || event.endpointName,
              consumableName: event.consumableName,
            };
          }
        });
      }
    });
    return map;
  }, [endpointsQueriesTable]);

  // Transform Locomotive flow rate data to table format
  const locoFlowRateTableData = useMemo(() => {
    const result: any[] = [];
    endpointsFlowRateQueriesTable.forEach((endpoint, idx) => {
      const endpointId = endpointsIds[idx];
      if (endpoint.data && Array.isArray(endpoint.data) && endpoint.data.length > 0) {
        // Get endpoint name - use Flow1, Flow2, etc. based on index
        // This will be replaced with actual names from chartData once it's computed
        const endpointName = `Flow${idx + 1}`; // Simple naming: Flow1, Flow2, etc.
        
        endpoint.data.forEach((item: any) => {
          // Transform LocoFlowMeterReadDto to table format
          // getLocoFlowMeter returns LocoFlowMeterReadDto[] where each item has singular fields:
          // volume, volumeTemperatureCompensated, flowRate, flowRateTemperatureCompensated, temperature
          const transformedItem = {
            timestamp: new Date(item.timestamp).getTime(),
            endpointId: endpointId,
            endpointName: endpointName,
            volume: item.volume ?? 0,
            volumeTemperatureCompensated: item.volumeTemperatureCompensated ?? 0,
            flowRate: item.flowRate ?? 0,
            flowRateTemperatureCompensated: item.flowRateTemperatureCompensated ?? 0,
            temperature: item.temperature ?? 0
          };
          result.push(transformedItem);
        });
      }
    });
    return result;
  }, [endpointsFlowRateQueriesTable, endpointsIds]);

  const mergedTableData = useMemo(() => {
    // Create maps for volume, pressure, and flow rate events
    const volumeMap = new Map();
    const pressureMap = new Map();
    const flowRateMap = new Map();
    
    // Store all volume events in the map
    eventTableData.forEach((volumeEvent: any) => {
      const key = `${volumeEvent.timestamp}_${volumeEvent.serialNo}`;
      volumeMap.set(key, volumeEvent);
    });

    // Store all pressure events in the map
    endpointsPressureQueriesTable.forEach(endpoint => {
      if (endpoint.data && Array.isArray(endpoint.data)) {
        endpoint.data.forEach((pressureEvent: any) => {
          const key = `${pressureEvent.timestamp}_${pressureEvent.serialNo}`;
          pressureMap.set(key, pressureEvent);
        });
      }
    });

    // Store all Locomotive flow rate events in the map
    // For flow meter data, we use timestamp_endpointId as key since there's no serialNo
    locoFlowRateTableData.forEach((flowRateEvent: any) => {
      const key = `${flowRateEvent.timestamp}_${flowRateEvent.endpointId || flowRateEvent.endpointName}`;
      flowRateMap.set(key, flowRateEvent);
    });

    // Get all unique keys from all maps
    const allKeys = new Set([...volumeMap.keys(), ...pressureMap.keys(), ...flowRateMap.keys()]);

    // Create merged data array with all events
    return Array.from(allKeys)
      .map(key => {
        const volumeEvent = volumeMap.get(key);
        const pressureEvent = pressureMap.get(key);
        const flowRateEvent = flowRateMap.get(key);
        const meta = serialNoToMeta[(pressureEvent || volumeEvent || flowRateEvent)?.serialNo] || {};
        
        // If we have flow rate event, merge it with volume/pressure
        if (flowRateEvent) {
          return {
            ...flowRateEvent,
            ...(volumeEvent && { volumeDispensed: volumeEvent.volumeDispensed, volumeDispensedRound: volumeEvent.volumeDispensedRound }),
            ...(pressureEvent && { 
              pressureMax: pressureEvent.pressureMax,
              pressureMean: pressureEvent.pressureMean,
              pressureMin: pressureEvent.pressureMin,
              temperature: pressureEvent.temperature || flowRateEvent.temperature
            }),
            endpointName: meta?.name || flowRateEvent.endpointName,
            consumableName: meta?.consumableName || volumeEvent?.consumableName
          };
        }
        
        return mergeEventData({ volumeEvent, pressureEvent, meta });
      })
      .filter((event): event is NonNullable<typeof event> => event !== null);
  }, [eventTableData, endpointsPressureQueriesTable, locoFlowRateTableData, serialNoToMeta]);

  const chartData = useMemo<ChartData>(() => {
    // Build maps for flow and pressure data by endpointId (injecting endpointId into the data)
    const flowRateMap = new Map<string, any>();
    endpointFlowRateQueries.forEach((endpoint, idx) => {
      const endpointId = endpointsIds[idx]; // The id used for this query
      if (endpoint.data) {
        flowRateMap.set(endpointId, { ...endpoint.data, endpointId });
      }
    });

    const pressureMap = new Map<string, any>();
    endpointsPressureQueries.forEach((endpoint, idx) => {
      const endpointId = endpointsIds[idx]; // The id used for this query
      if (endpoint.data) {
        pressureMap.set(endpointId, { ...endpoint.data, endpointId });
      }
    });

    // Get all unique endpointIds from both maps
    const allEndpointIds = Array.from(new Set([
      ...Array.from(flowRateMap.keys()),
      ...Array.from(pressureMap.keys())
    ]));

    const pressureResult = allEndpointIds.map(endpointId => {
      const flowData = flowRateMap.get(endpointId);
      const pressureData = pressureMap.get(endpointId);
      
      // Check if this is Locomotive - use same logic as queries
      // Check if data has Locomotive structure OR if asset machineryType is Locomotive
      // Check for volumesTempComp, flowRatesTempComp, or the longer names
      const hasLocoDataStructure = flowData && (
        flowData.volumesTempComp || 
        flowData.volumesTemperatureCompensated || 
        flowData.flowRatesTempComp || 
        flowData.flowRatesTemperatureCompensated || 
        flowData.volumes || 
        flowData.flowRates
      );
      const isLocomotive = assetMachineryType === 'Locomotive' || hasLocoDataStructure;

      let timestamps: number[] = [];
      let volumeDispensedData: number[] = [];
      let volume: number[] = [];
      let volumeTemperatureCompensated: number[] = [];
      let flowRate: number[] = [];
      let flowRateTemperatureCompensated: number[] = [];
      let temperature: number[] = [];
      
      if (isLocomotive && flowData) {
        // For Locomotives, use getLocoFlowMeterChartData structure
        // The API returns an object with arrays: { timestamps: [], volumes: [], volumesTemperatureCompensated: [], flowRates: [], flowRatesTemperatureCompensated: [], temperatures: [] }
        timestamps = Array.isArray(flowData.timestamps) ? flowData.timestamps : [];
        
        // Volume - prefer temperature compensated, fallback to regular
        // Check all possible field names: volumesTempComp, volumesTemperatureCompensated, volumeTemperatureCompensated
        volumeTemperatureCompensated = Array.isArray(flowData.volumesTempComp) 
          ? flowData.volumesTempComp 
          : (Array.isArray(flowData.volumesTemperatureCompensated) 
            ? flowData.volumesTemperatureCompensated 
            : (Array.isArray(flowData.volumeTemperatureCompensated) ? flowData.volumeTemperatureCompensated : []));
        volume = Array.isArray(flowData.volumes) 
          ? flowData.volumes 
          : (Array.isArray(flowData.volume) ? flowData.volume : []);
        
        // Flow Rate - prefer temperature compensated, fallback to regular
        // Check all possible field names: flowRatesTempComp, flowRatesTemperatureCompensated, flowRateTemperatureCompensated
        flowRateTemperatureCompensated = Array.isArray(flowData.flowRatesTempComp) 
          ? flowData.flowRatesTempComp 
          : (Array.isArray(flowData.flowRatesTemperatureCompensated) 
            ? flowData.flowRatesTemperatureCompensated 
            : (Array.isArray(flowData.flowRateTemperatureCompensated) ? flowData.flowRateTemperatureCompensated : []));
        flowRate = Array.isArray(flowData.flowRates) 
          ? flowData.flowRates 
          : (Array.isArray(flowData.flowRate) ? flowData.flowRate : []);
        
        // Temperature
        temperature = Array.isArray(flowData.temperatures) 
          ? flowData.temperatures 
          : (Array.isArray(flowData.temperature) ? flowData.temperature : []);
      } else if (flowData && Array.isArray(flowData.timestamps) && Array.isArray(flowData.volumeDispensedData) && flowData.timestamps.length === flowData.volumeDispensedData.length) {
        // For regular endpoints
        timestamps = flowData.timestamps;
        volumeDispensedData = flowData.volumeDispensedData;
      }

      return {
        endpointId,
        endpointName: flowData?.endpointName || pressureData?.endpointName || endpointId,
        endpointType: isLocomotive ? 'Locomotive' : (selectedItems?.find(item => item.id === endpointId)?.entityType || 'Unknown'),
        timestamps,
        volumeDispensedData,
        volume: Array.isArray(volume) ? volume : [],
        volumeTemperatureCompensated: Array.isArray(volumeTemperatureCompensated) ? volumeTemperatureCompensated : [],
        flowRate: Array.isArray(flowRate) ? flowRate : [],
        flowRateTemperatureCompensated: Array.isArray(flowRateTemperatureCompensated) ? flowRateTemperatureCompensated : [],
        temperature: Array.isArray(temperature) ? temperature : [],
        pressureData: {
          timestamps: pressureData?.timestamps || [],
          pressureDataMax: pressureData?.pressureDataMax || [],
          pressureDataMin: pressureData?.pressureDataMin || [],
          pressureDataMean: pressureData?.pressureDataMean || [],
          temperatureData: pressureData?.temperatureData || []
        }
      };
    });

    const visibleRange = {
      timestamps: pressureResult
        .map(endpoint => endpoint.timestamps)
        .flat()
        .sort((a, b) => a - b)
    };

    return {
      dateFilter: eventFilter,
      dataChart: pressureResult,
      visibleRange,
      aggregator
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointFlowRateQueries, endpointsPressureQueries, eventFilter, aggregator, endpointsIds, selectedItems]);

  const stateChartData = useMemo<StateChartData>(() => {
    const result = endpointFlowRateQueries.map((endpoint) => {
      
      let volume: number[] = [];
      let timestamps: number[] = [];
      
      if (endpoint.data !== undefined) {
        // Check if this is Locomotive data (has volumesTempComp, flowRatesTempComp, or the longer names)
        const hasLocoData = endpoint.data?.volumesTempComp || 
          endpoint.data?.volumesTemperatureCompensated || 
          endpoint.data?.volumes || 
          endpoint.data?.flowRatesTempComp || 
          endpoint.data?.flowRatesTemperatureCompensated || 
          endpoint.data?.flowRates;

        if (hasLocoData) {
          // For Locomotives, use getLocoFlowMeterChartData structure
          // Prefer volumesTempComp, fallback to volumesTemperatureCompensated, then volumes
          volume = Array.isArray(endpoint.data?.volumesTempComp)
            ? endpoint.data.volumesTempComp
            : (Array.isArray(endpoint.data?.volumesTemperatureCompensated)
              ? endpoint.data.volumesTemperatureCompensated
              : (Array.isArray(endpoint.data?.volumes) ? endpoint.data.volumes : []));
          timestamps = Array.isArray(endpoint.data?.timestamps) ? endpoint.data.timestamps : [];
        } else {
          // For regular endpoints
          volume = Array.isArray(endpoint.data?.volumeDispensedData) ? endpoint.data.volumeDispensedData : [];
          timestamps = Array.isArray(endpoint.data?.timestamps) ? endpoint.data.timestamps : [];
        }
      }
      
      // Ensure volume is always an array before scanning it
      const safeVolume = Array.isArray(volume) ? volume : [];
      const volumeExtent = arrayExtent(safeVolume);

      return {
        ...endpoint.data,
        timestamps: Array.isArray(timestamps) ? timestamps : [],
        maxVolume: safeVolume.length > 0 ? volumeExtent.max : 0,
        minVolume: safeVolume.length > 0 ? volumeExtent.min : 0,
      };
    }).sort((a, b) => (a.maxVolume || 0) > (b.maxVolume || 0) ? -1 : 1);

    const eventChartData = result.filter(endpoint => endpoint.timestamps?.length > 0);
    const visibleRange = { timestamps: eventChartData.map(endpoint => endpoint.timestamps).sort((a, b) => a > b ? 1 : -1) };

    return {
      dateFilter: eventFilter,
      alerts: null, // Will be set by the component
      statusData: null, // Will be set by the component
      timestamps: visibleRange
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointFlowRateQueries, eventFilter, selectedItems]);

  // Check if any endpoint is a Locomotive - use asset machineryType as primary source
  // Since we're using asset.machineryType in the queries, we should check the query data structure
  const hasLocomotive = endpointFlowRateQueries.some((q) => {
    // Check if query key indicates Locomotive
    const isLocoQuery = Array.isArray(q.queryKey) && q.queryKey[0] === 'locoFlowMeterChartData';
    // Or check if data has Locomotive structure (check for volumesTempComp, flowRatesTempComp, or longer names)
    const hasLocoData = q.data && (
      q.data.volumesTempComp || 
      q.data.volumesTemperatureCompensated || 
      q.data.flowRatesTempComp || 
      q.data.flowRatesTemperatureCompensated
    );
    return isLocoQuery || hasLocoData;
  });
  
  const hasPressureData = !hasLocomotive && chartData.dataChart.some(endpoint => endpoint?.pressureData?.timestamps?.length > 0);
  const hasFlowRateData = hasLocomotive || chartData.dataChart.some(endpoint => {
    const hasVolume = endpoint?.volume && Array.isArray(endpoint.volume) && endpoint.volume.length > 0;
    const hasFlowRate = endpoint?.flowRate && Array.isArray(endpoint.flowRate) && endpoint.flowRate.length > 0;
    return hasVolume || hasFlowRate;
  });

  // Update locoFlowRateTableData with endpoint names from chartData
  const locoFlowRateTableDataWithNames = useMemo(() => {
    return locoFlowRateTableData.map(item => {
      const chartEndpoint = chartData.dataChart.find(ep => ep.endpointId === item.endpointId);
      return {
        ...item,
        endpointName: chartEndpoint?.endpointName || item.endpointName
      };
    });
  }, [locoFlowRateTableData, chartData]);

  return {
    eventTableData,
    mergedTableData,
    locoFlowRateTableData: locoFlowRateTableDataWithNames,
    chartData,
    stateChartData,
    hasPressureData,
    hasFlowRateData,
    hasLocomotive
  };
};

const AllEndpointActivityTabPage = forwardRef<AllEndpointsActivityTabPageRef, Props>((props, ref) => {
  const localizationService = useLocalization();
  const queryClient = useQueryClient();
  const aggregator = useChartStore((state) => state.aggregator);
  const viewType = useChartStore(state => state.viewType);
  const setViewType = useChartStore(state => state.setViewType);



  const handleRefresh = async () => {
    // Invalidate all queries related to this endpoint
    await queryClient.invalidateQueries(['endpointTable']);
    await queryClient.invalidateQueries(['endpointPressureTable']);
    await queryClient.invalidateQueries(['endpointFlowRateChart']);
    await queryClient.invalidateQueries(['endpointPressureChart']);
    await queryClient.invalidateQueries(['StatusOnOffQuery']);
    await queryClient.invalidateQueries([ReactQueryKeys.AllAlertsQuery]);
  };

  useImperativeHandle(ref, () => ({
    refresh: handleRefresh
  }));

  // Set initial view type only when endpoints change
  useEffect(() => {
    if (props.endpointsIds.length > 1) {
      if (viewType !== 'table' && viewType !== 'userBar' && viewType !== 'userLine' && viewType !== 'line') {
        setViewType('line');
      }
    } else if (viewType !== 'userBar' && viewType !== 'userLine' && viewType !== 'table' && viewType !== 'bar') {
      setViewType('bar');
    }
  }, [props.endpointsIds, viewType, setViewType]);

  const {
    notificationsQuery,
    statusQuery,
    endpointsQueriesTable,
    endpointsPressureQueriesTable,
    endpointsFlowRateQueriesTable,
    endpointFlowRateQueries,
    endpointsPressureQueries
  } = useChartQueries(props, viewType, aggregator);

  const {
    eventTableData,
    mergedTableData,
    locoFlowRateTableData,
    chartData,
    stateChartData,
    hasPressureData,
    hasFlowRateData,
    hasLocomotive
  } = useChartData(
    endpointsQueriesTable,
    endpointsPressureQueriesTable,
    endpointsFlowRateQueriesTable,
    endpointFlowRateQueries,
    endpointsPressureQueries,
    props.eventFilter,
    aggregator,
    props.endpointsIds,
    props.selectedItems,
    props.asset
  );

  // Check if asset is Locomotive - use asset.machineryType directly for more reliable detection
  const isLocomotive = props.asset?.machineryType === 'Locomotive' || hasLocomotive;

  const {
    allDataSeries,
    visibleDataSeries,
    dataSeriesUpdated,
    setVisibleDataSeries
  } = useDataSeries(hasFlowRateData);



  // Filter notifications based on selected endpoints
  const filteredNotifications = useMemo(() => {
    if (!notificationsQuery.data || !props.endpointsIds || props.endpointsIds.length === 0) {
      return notificationsQuery.data;
    }

    // Handle the specific data structure: {timestamps: [], yValues: [], metadata: []}
    if (notificationsQuery.data && typeof notificationsQuery.data === 'object' && notificationsQuery.data.metadata) {
      // Get the actual selected injector names from the chart data
      const selectedInjectorNames = new Set<string>();
      
      // Map endpoint IDs to injector names using chart data
      if (chartData && chartData.dataChart) {
        chartData.dataChart.forEach(endpoint => {
          if (props.endpointsIds.includes(endpoint.endpointId)) {
            if (endpoint.endpointName) {
              selectedInjectorNames.add(endpoint.endpointName);
            }
          }
        });
      }
      
      // Filter the metadata array based on assignedToName
      const filteredMetadata = notificationsQuery.data.metadata.filter((metadataItem: any) => {
        // Check if the assignedToName matches any of the selected injectors
        return metadataItem.assignedToName && 
               Array.from(selectedInjectorNames).some(injectorName => 
                 metadataItem.assignedToName.includes(injectorName)
               );
      });

      // Create filtered timestamps and yValues arrays based on filtered metadata indices
      const filteredIndices = new Set();
      notificationsQuery.data.metadata.forEach((item: any, index: number) => {
        if (filteredMetadata.includes(item)) {
          filteredIndices.add(index);
        }
      });

      const filteredTimestamps = notificationsQuery.data.timestamps.filter((_: any, index: number) => 
        filteredIndices.has(index)
      );
      const filteredYValues = notificationsQuery.data.yValues.filter((_: any, index: number) => 
        filteredIndices.has(index)
      );

      return {
        timestamps: filteredTimestamps,
        yValues: filteredYValues,
        metadata: filteredMetadata
      };
    }

    // If it's not the expected structure, return as is
    return notificationsQuery.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsQuery.data, props.endpointsIds, props.selectedItems, chartData]);

  // Update state chart data with filtered notifications and status
  stateChartData.alerts = filteredNotifications;
  stateChartData.statusData = statusQuery.data;

  // Update series visibility when pressure or flow rate data availability changes
  useEffect(() => {
    if (hasPressureData) {
      allDataSeries.forEach(item => {
        if (!(item.id in visibleDataSeries)) {
          setVisibleDataSeries(item.id, item.id === 'volume');
        }
      });
    } else if (hasFlowRateData) {
      // For Flow Rate chart, initialize with only volume visible
      allDataSeries.forEach(item => {
        if (!(item.id in visibleDataSeries)) {
          setVisibleDataSeries(item.id, item.id === 'volume');
        }
      });
    }
  }, [hasPressureData, hasFlowRateData, visibleDataSeries, allDataSeries, setVisibleDataSeries]);

  const isLoading = 
    endpointsQueriesTable.some(result => result.isLoading) ||
    endpointsFlowRateQueriesTable.some(result => result.isLoading) ||
    endpointsPressureQueriesTable.some(result => result.isLoading) ||
    endpointFlowRateQueries.some(result => result.isLoading) ||
    notificationsQuery.isLoading ||
    notificationsQuery.isFetching ||
    statusQuery.isFetching ||
    endpointsQueriesTable.some(result => result.isFetching) ||
    endpointsFlowRateQueriesTable.some(result => result.isFetching) ||
    endpointsPressureQueriesTable.some(result => result.isFetching) ||
    endpointFlowRateQueries.some(result => result.isFetching) ||
    endpointsPressureQueries.some(result => result.isLoading) ||
    endpointsPressureQueries.some(result => result.isFetching);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // For table view, check if we have any data
  // For Locomotives, check locoFlowRateTableData
  // For regular endpoints, check mergedTableData or eventTableData
  const hasNoData = viewType === 'table' 
    ? (hasLocomotive && hasFlowRateData 
        ? locoFlowRateTableData.length === 0 
        : (mergedTableData.length === 0 && eventTableData.length === 0))
    : (endpointFlowRateQueries.length === 0 || !chartData.dataChart.some(endpoint => endpoint?.timestamps?.length > 0));

  if (hasNoData) {
    return <NoDataPage />;
  }




  return (
    <div className="">   
      <div>
        <ChartToolbar
          title={localizationService.toLanguageString('custom.events', enMessages.custom.events)}
          lastSeen={null}
          showAutoRange={true}
          showAggregatorOptions={!isLocomotive}
          showChartOptions={true}
          showThresholds={false}
          showSingleMultipleChart={false}
          showTable={true}
          showFilterDataSeries={hasPressureData || hasFlowRateData}
          allDataSeries={allDataSeries}
          onDataSeriesChange={dataSeriesUpdated}
          selectedDataSeries={allDataSeries.filter(item => visibleDataSeries[item.id])}
        />
        <div>
          {viewType !== 'table' && (
            hasPressureData ?
            <EndpointStatusChart 
              chartType="asset-pressure"
              key={`ActivityPressureChartAsset + ${props.isFullscreen}`} 
              statusChartData={stateChartData} 
              activityDataChart={chartData}
              visibleDataSeries={visibleDataSeries}
              isFullscreen={props.isFullscreen}
              chartParentHeight={props.chartParentHeight}
            />
            : hasFlowRateData ?
            <EndpointStatusChart 
              chartType="asset-flow-rate"
              key={`ActivityFlowRateChartAsset + ${props.isFullscreen}`} 
              statusChartData={stateChartData} 
              activityDataChart={chartData}
              visibleDataSeries={{
                volume: !!(visibleDataSeries.volume),
                volumeTemperatureCompensated: !!(visibleDataSeries.volumeTemperatureCompensated),
                flowRate: !!(visibleDataSeries.flowRate),
                flowRateTemperatureCompensated: !!(visibleDataSeries.flowRateTemperatureCompensated),
                temperature: !!(visibleDataSeries.temperature)
              }}
              isFullscreen={props.isFullscreen}
              chartParentHeight={props.chartParentHeight}
            />
            :
            <EndpointStatusChart 
              chartType="asset"
              key={`ActivityChartAsset`} 
              statusChartData={stateChartData} 
              activityDataChart={chartData}
              chartParentHeight={props.chartParentHeight}
            />
          )}
        </div>
        {viewType === 'table' && (
          <div className="">
            {/* For Locomotives, always use flow meter table */}
            {props.asset?.machineryType === 'Locomotive' ? (
              <EndpointFlowMeterTableAssets
                events={locoFlowRateTableData}
                aggregator={aggregator}
                legendConfig={visibleDataSeries}
                isMultiple={props.endpointsIds.length > 1}
              />
            ) : (
              <EndpointEventsTableAssets
                events={mergedTableData.length > 0 ? mergedTableData : eventTableData}
                aggregator={aggregator}
                hasPressureData={hasPressureData}
                legendConfig={visibleDataSeries}
              />
            )}
          </div>
        )}
      </div>
    </div> 
  );
});

export default AllEndpointActivityTabPage;